import { useMutation } from '@apollo/react-hooks';
import _ from 'lodash';
import { useObserver } from 'mobx-react-lite';
import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getLogger } from 'src/utils/datadog-logger';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

import { isRec } from 'shared/helpers/dispensaries';
import checkoutErrors from 'shared/core/fieldValidationErrorMessages';
import onClickSubmit from 'src/checkout/actions/on-click-submit';
import onClickSubmitV2 from 'src/checkout/actions/on-click-submit-v2';
import useStores from 'shared/hooks/use-stores';
import useBackwardsCompatibility from 'hooks/use-backwards-compatibility';
import useDispensaryPaymentMethods from 'shared/hooks/use-dispensary-payment-methods';
import useCart from 'shared/hooks/use-cart';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useSwitchMenuType from 'hooks/use-switch-menu-type';
import createOrderV2 from 'src/checkout/components/confirmation/create-order.gql';

import { tracker, useViewedCheckoutMonitor } from 'src/utils/analytics';
import { useViewedCheckoutAnalytics } from 'hooks/use-ecommerce-analytics';
import { AnalyticsOrder } from 'src/utils/analytics/events';
import { useDutchiePay } from 'src/payments/hooks';
import { DUTCHIE_PAY, LOG_NAMESPACES } from 'src/payments/constants';
import { contextStore } from 'checkout/context';
import { useTurnstile } from 'src/components/turnstile';
import { TURNSTILE_ACTIONS, TurnstileAction } from 'shared/constants/turnstile';
import { GqlOrders } from 'types/graphql';
import useKioskTerminals from '../use-kiosk-terminals';

type CreateOrderV2Response = {
  createOrderV2?: {
    valid: boolean;
    order: GqlOrders & { guestCustomer?: { email?: string } };
  };
};

type FormData = {
  birth: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  notes: string;
  destinationTerminal?: string;
};

type UseKioskCheckoutV2Params = {
  onClose: () => void;
  dispensary?: {
    id: string;
    name: string;
    kioskOrderingEnabled: boolean;
    orderTypesConfigV2?: {
      kiosk?: {
        anonymousCheckout?: boolean;
        directedOrders?: boolean;
      };
    };
  };
};

type UseKioskCheckoutV2Return = {
  formMethods: any; // TODO: Replace with proper type once TypeScript parsing is fixed
  loading: boolean;
  isAnonymousCheckout: boolean;
  setIsAnonymousCheckout: (value: boolean) => void;
  isCartEmpty: boolean;
  isTurnstileEnabled: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  TurnstileComponent: () => JSX.Element | null;
  handleSubmitOrder: () => Promise<void>;
  onSubmitFormError: (errors: Record<string, { type: string }>) => Promise<void>;
  onCloseModal: () => void;
  dispensary: UseKioskCheckoutV2Params['dispensary'];
};

export function useKioskCheckoutV2({
  onClose,
  dispensary: dispensaryFromProps,
}: UseKioskCheckoutV2Params): UseKioskCheckoutV2Return {
  const logger = getLogger(LOG_NAMESPACES.KIOSK_CHECKOUT);
  useViewedCheckoutMonitor();
  useViewedCheckoutAnalytics({ cart: useCart() });
  const flags = useFlags();
  const { dispensary: dispensaryFromHook } = useDispensary();
  const { menuType } = useCart();
  const formMethods = useForm<FormData>({
    defaultValues: {
      birth: '',
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      notes: '',
    },
  });

  const [loading, setLoading] = useState(false);
  const [isAnonymousCheckout, setIsAnonymousCheckout] = useState(false);
  const { Cart, UI } = useStores();
  const switchMenuType = useSwitchMenuType();
  const { User, apolloClient } = useBackwardsCompatibility();
  const allPaymentOptions = useDispensaryPaymentMethods() ?? [];
  const pbbEnabled = allPaymentOptions.some((option) => option.value === DUTCHIE_PAY && !option.disabled);
  const counterPaymentOptions = allPaymentOptions.filter((option) => option.value !== 'dutchiePay' && !option.disabled);

  const dispensary = dispensaryFromProps ?? dispensaryFromHook;
  const kioskTerminals = useKioskTerminals(dispensary);
  const isCartEmpty = useObserver(() => Cart.isEmpty);
  const { checkoutToken } = Cart;
  const { isDutchiePayEnabledForDispo, isEnrolledDutchiePay } = useDutchiePay();
  const turnstileTokenRef = useRef<string | null>(null);
  const turnstileErrorRef = useRef<string | null>(null);
  const { TurnstileComponent, executeAndWaitForToken: executeAndWaitForTurnstileToken } = useTurnstile({
    action: TURNSTILE_ACTIONS.CHECKOUT as TurnstileAction,
    size: 'flexible',
  });
  const isTurnstileEnabled = flags['growth.ecomm.enable-turnstile-on-kiosk-checkout.rollout'] ?? false;
  const isKioskProEnabled = useKioskPro();
  const isKioskLatencyManagementEnabled = flags['fintech.platform.kiosk-checkout-latency-management'] ?? false;

  const getCommonLogContext = (): Record<string, boolean | string> => ({
    dispensaryId: dispensary?.id ?? 'unknown',
    paymentMethod: Cart.paymentMethod ?? 'unknown',
    paymentMethodId: Cart.paymentMethodId ?? 'unknown',
    orderId: Cart.serverOrderId ?? 'unknown',
    pbbEnabled,
    checkoutToken,
  });

  // Setup effects
  useEffect(() => {
    if (menuType === 'med') {
      setIsAnonymousCheckout(false);
    }
  }, [menuType]);

  useEffect(() => {
    if (isCartEmpty) {
      onClose();
    }
  }, [isCartEmpty, onClose]);

  useEffect(() => {
    if (User.isLoggedIn && !isKioskProEnabled) {
      User.logout();
    }
  }, [User, isKioskProEnabled]);

  // Setup context
  contextStore.showErnie = UI.showErnie;
  contextStore.variant = UI.variant;
  contextStore.openOrderTypeModal = UI.openOrderTypeModal;

  // Setup cart defaults
  Cart.setOrderType(`pickup`);
  Cart.isKioskOrder = true;
  // When PBB is enabled, the payment method is set to DUTCHIE_PAY by default
  Cart.setPaymentMethod(pbbEnabled ? DUTCHIE_PAY : counterPaymentOptions[0].value);

  const useAnonymousKioskCheckout = dispensary?.orderTypesConfigV2?.kiosk?.anonymousCheckout && menuType === `rec`;

  function afterSubmit(data: CreateOrderV2Response): void {
    if (data.createOrderV2?.valid) {
      // clear order assigns the temporary estimate for the success modal
      const { order } = data.createOrderV2;
      const values = formMethods.getValues();
      const { email } = values;

      if (order.guestCustomer && !order.guestCustomer.email && !!email) {
        order.guestCustomer.email = email;
      }

      if (!isDutchiePayEnabledForDispo || !pbbEnabled) {
        tracker.placedOrder({
          checkoutToken,
          order: order as AnalyticsOrder,
          isDutchiePayEnabledForDispo,
          isEnrolledDutchiePay,
        });
      }

      // Track purchase analytics for customer GTM
      Cart.analyticsCartPurchase(order.orders, order, order.dispensary);

      onClose();

      Cart.setAnonymousOrderId(order.orderId);
      Cart.setServerOrderId(order.id);
      Cart.setOrderType(`pickup`);
      Cart.setUpdateOrderToken(order.updateOrderToken);
      formMethods.reset();

      if (isRec(dispensary)) {
        void switchMenuType(`rec`);
      }

      UI.updateShowKioskSelectPaymentModal(true);
    }

    // stop loading.
    setLoading(false);
  }

  const [submitOrder] = useMutation(createOrderV2, {
    client: apolloClient,
    onCompleted: afterSubmit,
  });

  async function handleSubmitOrder(): Promise<void> {
    if (loading) {
      return;
    }

    if (!dispensary?.kioskOrderingEnabled) {
      UI.showErnie(
        `Kiosk ordering is not currently enabled for ${String(dispensary?.name)}.
        Please contact dutchie support to enable this feature.`,
        `danger`
      );
      logger.error('Kiosk ordering disabled for dispensary', { ...getCommonLogContext() });
      onClose();
      return;
    }

    setLoading(true);

    if (isTurnstileEnabled) {
      if (!turnstileTokenRef.current) {
        try {
          const token = await executeAndWaitForTurnstileToken({ keepMounted: true, successDelayMs: 1000 });
          turnstileTokenRef.current = token;
        } catch (error) {
          turnstileErrorRef.current = (error as Error).message;
        }
      }

      if (turnstileErrorRef.current || !turnstileTokenRef.current || turnstileTokenRef.current.length === 0) {
        UI.showErnie(
          `Error validating your identity. Please try again. ${
            turnstileErrorRef.current ? `CF: ${turnstileErrorRef.current}` : ''
          }`,
          `danger`
        );
        logger.warn('Turnstile validation failed', {
          ...getCommonLogContext(),
          error: turnstileErrorRef.current ?? 'no token',
        });
        setLoading(false);
        return;
      }
    }

    const data = formMethods.getValues();
    const { notes, destinationTerminal } = data;
    let { birth = '', firstName = '', lastName = '', phone, email } = data;

    if (isAnonymousCheckout) {
      Cart.setIsAnonymousKioskCheckout(true);
      birth = '';
      firstName = `Anonymous`;
      lastName = `Order`;
      phone = '';
      email = '';
    }

    const [birthMonth, birthDay, birthYear] = _.split(birth, '/');

    const directedOrders = dispensary?.orderTypesConfigV2?.kiosk?.directedOrders;

    if (directedOrders && !_.isEmpty(kioskTerminals)) {
      if (!destinationTerminal) {
        UI.showErnie(`Please select a terminal for pickup`, `danger`);
        setLoading(false);
        logger.error('Directed orders require destination terminal');
        return;
      }

      Cart.setDestinationTerminal(destinationTerminal);
    }

    Cart.updateGuestCustomer(`birthMonth`, birthMonth);
    Cart.updateGuestCustomer(`birthDay`, birthDay);
    Cart.updateGuestCustomer(`birthYear`, birthYear);
    Cart.updateGuestCustomer(`firstName`, firstName);
    Cart.updateGuestCustomer(`lastName`, lastName);
    Cart.updateGuestCustomer(`phone`, phone);
    Cart.updateGuestCustomer(`email`, email);
    Cart.setSpecialInstructions(notes);

    // process submit action
    const handleClickSubmit = isKioskLatencyManagementEnabled ? onClickSubmitV2 : onClickSubmit;
    let result;
    try {
      result = await handleClickSubmit(submitOrder, {
        Cart,
        UI,
        User,
        apolloClient,
        showErnie: UI.showErnie,
        Checkout: contextStore,
        ...(useAnonymousKioskCheckout ? { isAnonymousCheckout } : {}),
        turnstileToken: turnstileTokenRef.current ?? undefined,
        refetchRetailerPaymentData: _.constant(Promise.resolve()),
        tracker,
        flags,
      });
    } catch (error) {
      console.error('Error submitting order:', error);
      logger.error('Kiosk checkout submit order error', {
        ...getCommonLogContext(),
        message: error?.message ?? 'unknown',
      });
      UI.showErnie(`Unable to submit order. Please try again.`, `danger`);
      return;
    } finally {
      setLoading(false);
    }

    if (result?.error && !_.isEmpty(result?.msg)) {
      const errorMsg = result?.msg;
      UI.showErnie(errorMsg, `danger`);
      logger.error('Kiosk checkout submit returned error', {
        ...getCommonLogContext(),
        message: errorMsg,
      });
    }
  }

  const onSubmitFormError = async (errors: Record<string, { type: string }>): Promise<void> => {
    const errorMessages: string[] = [];
    _.forEach(_.keys(errors), (name) => {
      const error = errors[name];
      if (error.type === 'required') {
        if (checkoutErrors[name]?.missing) {
          errorMessages.push(checkoutErrors[name].missing);
        } else if (name === 'birth') {
          errorMessages.push(checkoutErrors.birthday.missing);
        }
      }
    });

    const errorMessageToDisplay =
      errorMessages.length > 0 ? _.head(errorMessages) : `Please fill out the missing information`;

    UI.showErnie(errorMessageToDisplay, `danger`);
    setLoading(false);
    logger.error('Kiosk checkout form validation error', {
      ...getCommonLogContext(),
      message: errorMessageToDisplay,
    });
  };

  const onCloseModal = (): void => {
    onClose();
    UI.updateShowDutchiePayQrCodeInKiosk(false);
  };

  return {
    formMethods,
    loading,
    isAnonymousCheckout,
    setIsAnonymousCheckout,
    isCartEmpty,
    isTurnstileEnabled,
    TurnstileComponent,
    handleSubmitOrder,
    onSubmitFormError,
    onCloseModal,
    dispensary,
  };
}
